import { addDays, addMinutes, differenceInDays, format } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import RRule, { RRuleSet, rrulestr } from 'rrule'
import { getEventDate } from 'src/helpers/get-event-date'
import { EventPreview, ExtendEventPreview } from '../types/event.types'
import { updateRruleExdates } from './helpers'

export function groupEventsByDate(
  events: EventPreview[],
  startDate: Date,
  endDate: Date,
  timezone?: string,
): { date: string; isToday: boolean; events: ExtendEventPreview[] }[] {
  startDate = onlyDate(startDate)
  endDate = onlyDate(endDate)

  const totalDays = differenceInDays(endDate, startDate)

  const [nonRecurringEvents, recurringEvents] = splitEvents(events)

  const groupedEvents = Array.from({ length: totalDays }).map((_, i) => {
    const currentDate = addDays(startDate, i)

    // Generate occurrences only for this single day
    const dayStart = currentDate
    const dayEnd = addMinutes(addDays(currentDate, 1), -1)

    const expandedRecurringEvents = expandRecurringEventsForDay(recurringEvents, dayStart, dayEnd)
    const allEvents = [...expandedRecurringEvents, ...nonRecurringEvents]

    const dateLabel = timezone
      ? formatInTimeZone(currentDate, timezone, 'EEE, LLL d')
      : format(currentDate, 'EEE, LLL d')

    return {
      date: dateLabel,
      isToday: isTodayInTimezone(currentDate, timezone),
      events: dedupeByIdAndDate(allEvents.filter((event) => isEventOnSameDay(currentDate, event, timezone))).sort(
        (event1, event2) => compareEventsByTime(event1, event2),
      ),
    }
  })

  return groupedEvents
}

function isTodayInTimezone(currentDate: Date, timezone?: string): boolean {
  if (!timezone) {
    const today = new Date()
    return (
      currentDate.getFullYear() === today.getFullYear() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getDate() === today.getDate()
    )
  }
  const now = new Date()
  const todayInTz = formatInTimeZone(now, timezone, 'yyyy-MM-dd')
  const currentInTz = formatInTimeZone(currentDate, timezone, 'yyyy-MM-dd')
  return todayInTz === currentInTz
}

function splitEvents(events: EventPreview[]): [EventPreview[], EventPreview[]] {
  const isGoogleEvent = (event: any) => Boolean(event.google_event_date)

  // When listing from Google, we don't need to split events, treat
  // all events as non-recurring, because Google already expanded the events
  // into individual occurrences and we don't need to expand them again.
  const nonRecurringEvents = events
    .filter((event) => (isGoogleEvent(event) ? true : !event.recurrence_rules))
    .map((event) => mergeParentIfRecurringChild(event, events))

  // Otherwise, we need to handle recurring events ourselves.
  const recurringEvents = dedupeById(events.filter((event) => !isGoogleEvent(event) && event.recurrence_rules))

  return [nonRecurringEvents, recurringEvents]
}

function dedupeById(events: EventPreview[]): EventPreview[] {
  const seen = new Map<string, EventPreview>()
  for (const event of events) {
    if (!seen.has(event.id)) {
      seen.set(event.id, event)
    }
  }
  return [...seen.values()]
}

function dedupeByIdAndDate(events: ExtendEventPreview[]): ExtendEventPreview[] {
  const seen = new Map<string, ExtendEventPreview>()
  for (const event of events) {
    const key = `${event.id}-${event.event_date}`
    if (!seen.has(key)) {
      seen.set(key, event)
    }
  }
  return [...seen.values()]
}

function onlyDate(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function mergeParentIfRecurringChild(event: EventPreview, events: EventPreview[]): ExtendEventPreview {
  const original_event_date = event.event_date
  if (event.recurring_event_id !== null) {
    const parentEvent = events.find((e) => e.event_id === event.recurring_event_id)
    if (parentEvent) {
      return { ...parentEvent, ...event, original_event_date }
    }
  }
  return { ...event, original_event_date }
}

function expandRecurringEventsForDay(recurringEvents: EventPreview[], startDate: Date, endDate: Date): EventPreview[] {
  return recurringEvents.flatMap((recurringEvent) => {
    const recurrenceRuleSet = buildRuleSetForEvent(recurringEvent)
    const occurrences = recurrenceRuleSet.between(startDate, endDate, true)

    return occurrences.map((occurrenceDateUTC) => ({
      ...recurringEvent,
      event_date: applyOriginalEventTime(occurrenceDateUTC, recurringEvent.event_date).toString(),
      original_event_date: recurringEvent.event_date,
    }))
  })
}

function buildRuleSetForEvent(recurringEvent: EventPreview): RRuleSet {
  const rules = [...recurringEvent.recurrence_rules]
  const originalEventDateUTC = new Date(parseInt(recurringEvent.event_date, 10))

  rules.push(RRule.optionsToString({ dtstart: originalEventDateUTC }))

  const recurrenceRuleSet = rrulestr(rules.join('\n')) as RRuleSet
  updateExdatesIfNeeded(recurrenceRuleSet, originalEventDateUTC, 0)

  return recurrenceRuleSet
}

function updateExdatesIfNeeded(ruleSet: RRuleSet, dtstart: Date, offset: number): void {
  const extendedRuleSet = ruleSet as RRuleSet & { _exdate: Date[] }
  if (extendedRuleSet._exdate) {
    extendedRuleSet._exdate = updateRruleExdates(dtstart, ruleSet, false, 0)
  }
}

function applyOriginalEventTime(occurrence: Date, originalEventDateString: string): number {
  const originalEventDate = new Date(parseInt(originalEventDateString, 10))

  occurrence.setUTCHours(originalEventDate.getUTCHours())
  occurrence.setUTCMinutes(originalEventDate.getUTCMinutes())
  occurrence.setUTCSeconds(originalEventDate.getUTCSeconds())
  occurrence.setUTCMilliseconds(originalEventDate.getUTCMilliseconds())

  return occurrence.getTime()
}

type EventPreviewWithGoogleDate = EventPreview & { google_event_date?: string }

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/** Date-only prefix from Google Calendar (`YYYY-MM-DD` or start of an ISO string). */
function parseAllDayDateKeyFromGoogleEventDate(raw: string): string | null {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(raw.trim())
  return m?.[1] ?? null
}

/**
 * All-day events are calendar dates, not instants. Bucketing must not run them through a display timezone
 * (which shifts UTC-midnight storage to the previous/next local day).
 */
function getAllDayEventCalendarDateKey(event: EventPreviewWithGoogleDate): string | null {
  const google = event.google_event_date?.trim()
  if (google) {
    const key = parseAllDayDateKeyFromGoogleEventDate(google)
    if (key) return key
  }
  const ms = Number(event.event_date)
  if (!Number.isFinite(ms)) return null
  const d = new Date(ms)
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`
}

function localCalendarDateKey(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

function isEventOnSameDay(currentDay: Date, event: EventPreview, timezone?: string): boolean {
  if (event.all_day) {
    const allDayKey = getAllDayEventCalendarDateKey(event as EventPreviewWithGoogleDate)
    if (!allDayKey) return false
    if (timezone) {
      return allDayKey === formatInTimeZone(currentDay, timezone, 'yyyy-MM-dd')
    }
    return allDayKey === localCalendarDateKey(currentDay)
  }

  const eventDate = getEventDate(event)
  if (timezone) {
    return formatInTimeZone(eventDate, timezone, 'yyyy-MM-dd') === formatInTimeZone(currentDay, timezone, 'yyyy-MM-dd')
  }
  return (
    eventDate.getFullYear() === currentDay.getFullYear() &&
    eventDate.getMonth() === currentDay.getMonth() &&
    eventDate.getDate() === currentDay.getDate()
  )
}

function compareEventsByTime(event1: EventPreview, event2: EventPreview): number {
  // All-day events should be sorted to the top
  if (event1.all_day && !event2.all_day) {
    return -1
  }
  if (!event1.all_day && event2.all_day) {
    return 1
  }
  // If both events are all-day, sort by title
  if (event1.all_day && event2.all_day) {
    return (event1.title ?? '').localeCompare(event2.title ?? '')
  }
  // Otherwise, sort by event_date
  const time1 = parseInt(event1.event_date, 10)
  const time2 = parseInt(event2.event_date, 10)
  return time1 - time2
}
